<template>
	<div class="animated fadeIn">
		<CommandHeaderV2 :command_service_id="service_id" :record_id="command_id" active="variables">
			<div class="pt-3 pr-3 pb-0 pl-3">
				<!-- START VARIABLE INFO CARD -->
				<b-card>
					<div slot="header">
						<strong>{{ $t(variable_id == 0 ? 'Add' : 'Edit') }} {{ $t('Variable Info') }}</strong>
					</div>
					<tooltip label="Clone attributes from another variable" placement="right">
						<b-form-group label-for="elementsAppendButton">
							<b-input-group>
								<select class="form-control" v-model="selected_var" :disabled="!all_variables.length">
									<option value="0">{{ $t('Choose one') }}</option>
									<option
										v-for="(v, vi) in all_variables"
										:value="v.var_id"
										:key="'clone_var' + vi"
									>{{ v.var_name }}</option>
								</select>
								<b-input-group-append>
									<b-button variant="warning" @click="clone_var()">{{ $t('Clone') }}</b-button>
								</b-input-group-append>
							</b-input-group>
						</b-form-group>
					</tooltip>
					<tooltip label="Variable Name" placement="right">
						<input
							name="variable_name"
							v-model="record.var_name"
							v-validate="'required|alpha_dash'"
							:class="{'form-control': true, 'is-invalid': errors.has('variable_name')}"
							type="text"
						/>
						<span
							v-if="errors.has('variable_name')"
							class="help is-danger invalid-feedback"
						>{{ $t(errors.first('variable_name')) }}</span>
					</tooltip>
					<tooltip label="Prompt" placement="right">
						<textarea
							name="variable_prompt"
							v-model="record.var_prompt"
							:class="{'form-control': true, 'is-invalid': errors.has('variable_prompt')}"
							:placeholder="$t('Prompt')"
						></textarea>
						<span
							v-if="errors.has('variable_prompt')"
							class="help is-danger invalid-feedback"
						>{{ $t(errors.first('variable_prompt')) }}</span>
					</tooltip>
					<tooltip label="Order" placement="right">
						<input
							name="var_order"
							v-model="record.var_order"
							v-validate="'required|integer'"
							:class="{'form-control': true, 'is-invalid': errors.has('var_order')}"
							type="text"
						/>
						<span
							v-if="errors.has('var_order')"
							class="help is-danger invalid-feedback"
						>{{ $t(errors.first('var_order')) }}</span>
					</tooltip>
					<tooltip label="Type" placement="right">
						<select class="form-control" v-model="record.var_type" v-on:change="handleOnChangeVarType">
							<option value disabled>{{ $t('choose one') }}</option>
							<option
								v-for="(svar) in $store.state.command.variables.types"
								:value="svar.value"
								:key="svar.value"
							>{{ $t(svar.label) }}</option>
						</select>
					</tooltip>
					<tooltip
						label="Component"
						placement="right"
						v-if="all_components.length > 0 && record.var_type == 'component'"
					>
						<select class="form-control" v-model="record.entity_name">
							<optgroup
								:label="comp.name"
								v-for="comp in all_components"
								:key="`component-group-${comp.name}`"
							>
								<option
									v-for="entityClass in comp.classes"
									:value="entityClass"
									:key="`entity-class-${entityClass}-${comp.name}`"
								>{{ entityClass }}</option>
							</optgroup>
						</select>
					</tooltip>

					<tooltip label="Text" placement="right" v-if="record.var_type == 'text'">
						<input
							name="var_val_text"
							v-model="record.var_val_text"
							v-validate="record.var_type == 'text' ? 'required' : ''"
							:class="{'form-control': true, 'is-invalid': errors.has('var_val_text')}"
						/>
						<span
							v-if="errors.has('var_val_text')"
							class="help is-danger invalid-feedback"
						>{{ $t(errors.first('var_val_text')) }}</span>
					</tooltip>
					<tooltip label="Expression" placement="right" v-if="record.var_type == 'regex'">
						<input
							name="var_val_regex"
							v-model="record.var_val_regex"
							v-validate="record.var_type == 'regex' ? 'required' : ''"
							:class="{'form-control': true, 'is-invalid': errors.has('var_val_regex')}"
						/>
						<span
							v-if="errors.has('var_val_regex')"
							class="help is-danger invalid-feedback"
						>{{ $t(errors.first('var_val_regex')) }}</span>
					</tooltip>
					<tooltip label="Stopwords" placement="right" v-if="record.var_type == 'search'">
						<input-tag
							:placeholder="$t('Type') + ' ' + $t('Stopwords')"
							:tags.sync="record.var_val_stopwords"
							:add-tag-on-keys="[13]"
							:addTagOnBlur="true"
						></input-tag>
					</tooltip>

					<template v-if="record.var_type == 'list'">
						<tooltip label="Items" placement="right">
							<input-tag
								:placeholder="$t('Type') + ' ' + $t('Items')"
								:tags.sync="record.var_val_list"
								:add-tag-on-keys="[13]"
								:addTagOnBlur="true"
							></input-tag>
						</tooltip>

						<!-- START VARIABLE KEYWORDS CARD -->
						<div class="form-group">
							<tooltip label="keyword" placement="right">
								<select class="form-control" v-model="lbl.keyword" @change="handleOnChangeSelectSynonym">
									<option v-for="item in record.var_val_list" :value="item" :key="item">{{ item }}</option>
								</select>
							</tooltip>

							<tooltip label="Synonyms" placement="right">
								<input-tag
									:tags.sync="lbl.syns"
									:add-tag-on-keys="[13]"
									:addTagOnBlur="true"
									:placeholder="$t('Synonyms')"
								></input-tag>
							</tooltip>
							<div class="form-action">
								<button
									type="button"
									@click="addSynonym()"
									class="btn btn-primary btn-sm"
								>{{ $t('Save synonyms') }}</button>
							</div>
						</div>
						<!-- END VARIABLE KEYWORDS CARD -->
					</template>

					<div class="row" v-if="record.var_type == 'datasetList'">
						<div class="col-sm-12 col-md-6">
							<tooltip label="Choose Dataset" placement="right">
								<select
									:disabled="!(datasets && Array.isArray(datasets))"
									class="form-control"
									v-model="record.var_type_ds_id"
									v-on:change="
												;(record.var_type_ds_field_where = null),
													(record.var_type_ds_field_return = null),
													get_ds_selected_schema()
											"
								>
									<option disabled value="0">
										{{
										$t(
										datasets.length
										? 'Choose One'
										: 'No Datasets Currently Registered'
										)
										}}
									</option>
									<option v-for="(ds) in datasets" :key="ds.ds_id" :value="ds.ds_id">{{ ds.ds_name }}</option>
								</select>
							</tooltip>
						</div>
						<div class="col-sm-12 col-md-6">
							<tooltip label="Choose Structure Name" placement="right">
								<select
									:disabled="!ds_schema.length"
									class="form-control"
									v-model="record.var_type_ds_field_where"
									v-on:change="handleOnChangeDsShema"
								>
									<option disabled :value="null">{{ $t('Choose One') }}</option>
									<option v-for="sh in ds_schema" :key="sh.name" :value="sh.name">{{ sh.name }}</option>
								</select>
							</tooltip>
						</div>
						<div class="col-sm-12">
							<tooltip label="Items List" placement="right">
								<input-tag
									:placeholder="$t('Type') + ' ' + $t('Items List')"
									:tags.sync="record.var_val_list"
									:add-tag-on-keys="[13]"
									:addTagOnBlur="true"
								></input-tag>
							</tooltip>
						</div>
					</div>
					<div class="row" v-if="record.var_type == 'search_DS'">
						<div class="col-sm-12 col-md-4">
							<tooltip label="Dataset" placement="right">
								<select
									:disabled="!(datasets && Array.isArray(datasets))"
									class="form-control"
									v-model="record.var_type_ds_id"
									v-on:change="
												;(record.var_type_ds_field_where = null),
													(record.var_type_ds_field_return = null),
													get_ds_selected_schema()
											"
								>
									<option disabled value="0">
										{{
										$t(
										datasets.length
										? 'Choose One'
										: 'No Datasets Currently Registered'
										)
										}}
									</option>
									<option
										v-for="ds in datasets"
										:key="'search_DS_' + ds.ds_id"
										:value="ds.ds_id"
									>{{ ds.ds_name }}</option>
								</select>
							</tooltip>
						</div>
						<div class="col-sm-12 col-md-4">
							<tooltip label="like field" placement="right">
								<select
									:disabled="!ds_schema.length"
									class="form-control"
									v-model="record.var_type_ds_field_where"
									v-on:change="set_var_type_return_field_default"
								>
									<option disabled :value="null">
										{{
										$t(
										!datasets.length || !ds_schema.length
										? 'No Fields '
										: 'Choose One'
										)
										}}
									</option>
									<option v-for="sh in ds_schema" :key="sh.name" :value="sh.name">{{ sh.name }}</option>
								</select>
							</tooltip>
						</div>
						<div class="col-sm-12 col-md-4">
							<tooltip label="return field" placement="right">
								<select
									:disabled="!ds_schema.length"
									class="form-control"
									v-model="record.var_type_ds_field_return"
								>
									<option disabled :value="null">
										{{
										$t(
										!datasets.length || !ds_schema.length
										? 'No Fields'
										: 'Choose One'
										)
										}}
									</option>
									<option
										v-for="sh in ds_schema"
										:key="'return_field_' + sh.name"
										:value="sh.name"
									>{{ sh.name }}</option>
								</select>
							</tooltip>
						</div>
					</div>
					<tooltip label="Default Value Type" placement="right" v-if="all_preferences.length > 0">
						<select class="form-control" v-model="record.var_default_val_type">
							<option value="txt">{{ $t('Text') }}</option>
							<option value="prf">{{ $t('Preference') }}</option>
						</select>
					</tooltip>
					<tooltip label="Default Value" placement="right" v-if="record.var_default_val_type == 'txt'">
						<input name="var_default_val" v-model.trim="record.var_default_val" class="form-control" />
					</tooltip>
					<tooltip label="Preference" placement="right" v-if="record.var_default_val_type == 'prf'">
						<select class="form-control" v-model="record.var_default_val_preference_id">
							<option value="0">{{ $t('No Preference') }}</option>
							<template v-for="pref in all_preferences">
								<option
									v-if="pref.var_id != record_id"
									:key="pref.var_id"
									:value="pref.var_id"
								>{{ pref.var_name }}</option>
							</template>
						</select>
					</tooltip>
					<tooltip label="Is Preference" placement="top">
						<c-switch type="default" variant="primary" :pill="true" v-model="record.var_is_preference" />
					</tooltip>

					<tooltip label="Preference timeout" placement="right">
						<input
							name="var_is_preference_timeout"
							:disabled="!record.var_is_preference"
							v-model="record.var_is_preference_timeout"
							class="form-control"
							v-validate="'integer|min:1|max:11'"
							type="text"
							:placeholder="$t('Preference timeout')"
							:class="{'form-control': true, 'is-invalid': errors.has('var_is_preference_timeout')}"
						/>
						<span
							v-if="errors.has('var_is_preference_timeout')"
							class="help is-danger invalid-feedback"
						>{{ $t(errors.first('var_is_preference_timeout')) }}</span>
					</tooltip>
				</b-card>
				<!-- END VARIABLE INFO CARD -->

				<!-- START VARIABLE FALLBACK CARD -->
				<b-card>
					<div slot="header">
						<strong>{{ $t('Fallback') }}</strong>
					</div>
					<tooltip label="Fallback Type" placement="right">
						<select class="form-control" v-model="record.var_fallback_type">
							<option value="text">{{ $t('Text') }}</option>
							<option value="options">{{ $t('Options') }}</option>
							<option value="redir">{{ $t('Redirect to another command') }}</option>
						</select>
					</tooltip>
					<tooltip label="Command" placement="right" v-if="record.var_fallback_type == 'redir'">
						<select class="form-control" v-model="record.var_fallback_redirect_command_id">
							<option
								v-for="command in all_commands"
								:key="command.command_id"
								:value="command.command_id"
							>{{ command.service_name }} - {{ command.command_name }}</option>
						</select>
					</tooltip>

					<tooltip
						label="Fallback Message"
						placement="right"
						v-if="['text', 'options'].indexOf(record.var_fallback_type)>= 0"
					>
						<textarea
							v-autocomplete
							name="var_fallback_msg"
							v-model="record.var_fallback_msg"
							class="form-control"
						></textarea>
					</tooltip>

					<div v-if="record.var_fallback_type == 'options'">
						<table class="table table-bordered table-striped table-sm">
							<thead>
								<tr>
									<td>{{ $t('Lable') }}</td>
									<td>{{ $t('Value') }}</td>
									<td>{{ $t('Edit') }}</td>
									<td>{{ $t('Delete') }}</td>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(opt, index) in record.var_fallback_options" :key="'fallmsg_' + index">
									<td>{{ opt.label }}</td>
									<td>{{ opt.val }}</td>
									<td>
										<i @click="edit_opt_mode(index)" class="fa fa-edit fa-lg"></i>
									</td>
									<td>
										<i @click="remove_opt(index)" class="fa fa-remove fa-lg"></i>
									</td>
								</tr>
								<tr v-if="!record.var_fallback_options.length" v-cloak>
									<td colspan="4">
										<div class="text-center">{{ $t('no data') }}!</div>
									</td>
								</tr>
							</tbody>
						</table>
						<div class="form-group row">
							<div class="col-sm-5">
								<tooltip label="label" placement="right">
									<input type="text" v-model="opt.label" class="form-control" />
								</tooltip>
							</div>
							<div class="col-sm-5">
								<tooltip label="value" placement="right">
									<input type="text" v-model="opt.val" class="form-control" />
								</tooltip>
							</div>
							<div class="btn-action col-sm-2">
								<button
									v-if="opt_mode == 'add'"
									@click="add_opt()"
									class="btn btn-primary btn-sm"
								>{{ $t('Add') }}</button>
								<button
									v-if="opt_mode == 'edit'"
									@click="edit_opt()"
									class="btn btn-primary btn-sm"
								>{{ $t('Edit') }}</button>
							</div>
						</div>
					</div>
				</b-card>
				<!-- END VARIABLE FALLBACK CARD -->
				<!-- START VARIABLE CURSOR CARD -->
				<b-card>
					<div slot="header">
						<strong>{{ $t('Cursor') }}</strong>
					</div>
					<tooltip label="Cursor Variable" placement="right">
						<select
							class="form-control"
							v-model="record.var_cursor_var_id"
							:disabled="!all_command_variables.length"
						>
							<option value="0">{{ $t( 'Choose One' ) }}</option>
							<option v-for="v in all_command_variables" :key="v.var_id" :value="v.var_id">{{ v.var_name }}</option>
						</select>
					</tooltip>
				</b-card>
				<!-- END VARIABLE CURSOR CARD -->
				<button-save @click="save"></button-save>
			</div>
		</CommandHeaderV2>
	</div>
</template>

<script>
	import axios from 'axios'

	import InputTag from 'vue-input-tag'
	import CommandHeaderV2 from '../../../components/CommandHeaderV2'
	import {cSwitch} from '../../../components'
	import buttonSave from '../../../components/ButtonSave'

	/**
	 * @vuese
	 *
	 * @group CommandResponse
	 */
	export default {
		name: 'VariableForm',
		components: {
			cSwitch,
			CommandHeaderV2,
			InputTag,
			buttonSave,
		},
		computed: {
			// varValTextRules() {
			//     return 'required';
			// },
		},
		data() {
			return {
				/**
				 * @vuese
				 */
				variable_id: this.$route.params.variable_id,
				record: {
					var_name: '',
					var_type: '',
					var_type_ds_id: 0,
					var_type_ds_field_where: null,
					var_type_ds_field_return: null,
					var_prompt: '',
					var_is_preference: false,
					var_is_preference_timeout: 0,
					var_order: 1,
					var_val_regex: '',
					var_val_text: '',
					entity_name: '',
					var_val_stopwords: [],
					var_val_list: [],
					var_labels: [],
					var_default_val_type: 'txt',
					var_default_val: '',
					var_default_val_preference_id: 0,
					var_cursor_var_id: 0,
					var_fallback_type: 'text',
					var_fallback_redirect_command_id: 0,
					var_fallback_msg: '',
					var_fallback_options: [],
				},
				all_variables: [],
				all_components: [],
				all_command_variables: [],
				selected_var: 0,
				lbl_mode: 'add',
				active_lbl_index: -1,
				lbl: {
					keyword: '',
					syns: [],
				},
				opt_mode: 'add',
				active_opt_index: -1,
				opt: {
					label: '',
					val: '',
				},
				service_id: 0,
				command_id: 0,
				record_id: 0,
				all_preferences: [],
				all_commands: [],
				err: [],
				datasets: false,
				ds_schema: [],
			}
		},
		methods: {
			set_var_type_return_field_default(e) {
				this.record.var_type_ds_field_return = e.target.value
			},

			clone_var() {
				if (this.selected_var) {
					this.$root.loader = true
					var var_name = this.record['var_name']
					axios.get('/command_var/browse/' + this.selected_var, {}).then((response) => {
						if (response.data.status == 1) {
							this.record = response.data.data
							this.record['var_name'] = var_name
							this.record['record_id'] = this.record_id

							this.selected_var = 0
						}
						this.$root.loader = false
					})
				}
			},

			handleOnChangeSelectSynonym(e) {
				var keyword = e.target.value.toString(),
					label = this.getLabel(keyword, this.record.var_labels)
				this.lbl.syns = label.syns
			},

			getLabel(keyword, items) {
				for (let i = 0; i < items.length; i++) {
					const element = items[i]
					if (element.keyword == keyword) return element
				}

				var item = {
					keyword,
					syns: [],
				}

				this.record.var_labels.push(item)

				return this.record.var_labels[this.record.var_labels.length - 1]
			},

			addSynonym() {
				var keyword = this.lbl.keyword.toString()

				for (let i = 0; i < this.record.var_labels.length; i++) {
					const element = this.record.var_labels[i]
					if (element.keyword == keyword) {
						element.syns = this.lbl.syns
					}
				}

				this.lbl = {
					keyword: '',
					syns: [],
				}
			},

			add_opt() {
				if (this.opt.label != '' && this.opt.val != '' && !this.check_exist_option(this.opt.label)) {
					this.record.var_fallback_options.push(this.opt)
					this.opt = {
						label: '',
						val: '',
					}
				}
			},
			check_exist_option(label) {
				for (var i = 0; i < this.record.var_fallback_options.length; i++) {
					if (this.record.var_fallback_options[i].label == label) {
						return true
					}
				}
				return false
			},
			remove_opt(index) {
				this.record.var_fallback_options.splice(index, 1)
			},
			edit_opt_mode(index) {
				this.opt_mode = 'edit'
				this.active_opt_index = index
				this.opt = this.record.var_fallback_options[index]
				return true
			},
			edit_opt() {
				if (this.active_opt_index >= 0 && this.opt.label != '' && this.opt.val != '') {
					for (var i = 0; i < this.record.var_fallback_options.length; i++) {
						if (i == this.active_opt_index) {
							this.record.var_fallback_options[i] = this.opt
						}
					}
					this.active_opt_index = -1
					this.opt_mode = 'add'
					this.opt = {
						label: '',
						val: '',
					}
				}
			},
			get_all_preferences() {
				this.$root.loader = true
				axios.post('/command_var/get_all_preferences/0', {}, {}).then((response) => {
					if (response.data.status == 1) {
						this.all_preferences = response.data.data
						// console.log(this.all_preferences);
					}
					this.$root.loader = false
				})
			},

			save() {
				let varType = (this.record.var_type || '').trim()

				this.$validator.validateAll().then((result) => {
					this.err = []
					if (this.record.var_name.trim() == '') {
						this.err.push('Please enter the variable name')
					}
					if (varType == 'text') {
						if (this.record.var_val_text.trim() == '') {
							this.err.push('Please enter the variable text')
						}
					} else if (varType == 'regex') {
						if (this.record.var_val_regex.trim() == '') {
							this.err.push('Please enter the variable regex')
						}
					} else if (varType == 'list' || varType == 'datasetList') {
						if (this.record.var_val_list.length == 0) {
							this.err.push('Please enter the variable values list')
						}
					}

					if (this.record.var_is_preference) {
						this.record.var_is_preference = 1
					} else {
						this.record.var_is_preference = 0
					}

					if (!this.err.length) {
						this.$root.loader = true
						if (varType === 'datasetList') this.record.var_type = 'list'
						axios
							.post('/command_var/save/' + this.record_id + '/' + this.command_id, this.record, {})
							.then((response) => {
								if (response.data.status) {
									this.$root.notify({
										status: 'success',
										message: 'Variable Saves Successfully',
										area: 'topRight',
									})
									if (parseInt(this.record_id)) {
										this.$store.commit('update_go_variables', [this.record_id, this.record.var_name])
									} else {
										this.$store.commit('add_go_variables', {
											var_id: response.data.id,
											var_name: this.record.var_name,
										})
									}
									this.$store.commit('update_breadcrumb_history', {
										mode: 'variables',
										id: this.record_id,
										change_to: this.record.var_name,
									})
									this.$router.push(
										'/services/' +
											this.$route.params.service_id +
											'/commands/' +
											this.$route.params.command_id +
											'/variables/' +
											response.data.id
									)
								} else {
									this.$root.notify({
										status: 'error',
										message: response.data.msg,
										area: 'topRight',
									})
								}

								this.$root.loader = false
							})
					} else {
					}
				})
			},

			handleOnChangeVarType(e) {
				switch (e.target.value) {
					case 'datasetList':
					case 'search_DS':
						if (this.datasets === false) {
							// this.$root.loader = true;
							axios.get('/dataset/get_list', {}).then((res) => {
								if (res.data.status == 1) {
									var datasets = [].concat(res.data.private, res.data.public)
									if (datasets.length) this.datasets = datasets
									else this.datasets = false
								}
							})
						}
						break
				}
			},
			get_ds_selected_schema() {
				if (this.datasets) {
					var travers = function(list, id) {
						if (!list.length) return []
						var f = list.splice(0, 1)[0]
						if (f.ds_id === id) return f.ds_schema
						return travers(list, id)
					}
					this.ds_schema = travers(this.datasets.slice(0), this.record.var_type_ds_id)
				}
			},
			handleOnChangeDsShema() {
				axios
					.post(
						'/dataset/find',
						{
							ds_id: this.record.var_type_ds_id,
							field_name: this.record.var_type_ds_field_where,
						},
						{}
					)
					.then((res) => {
						if (res.data.status) {
							let list = [].concat(this.record.var_val_list, res.data.data).reduce((l, c) => {
								for (var i = l.length - 1; i >= 0; i--) {
									if (l[i] === c) {
										return l
									}
								}
								return l.concat(c)
							}, [])
							this.record.var_val_list = list
						}
					})
			},
		},
		mounted() {
			// get all variables
			axios.post('/command_var/get_all/0', {}, {}).then((response) => {
				if (response.data.status == 1) {
					this.all_variables = response.data.data
				}
			})

			// get all components
			axios.get('/components/entities').then((response) => {
				this.all_components = response.data.components
				this.$root.loader = false
			})

			// get all commands
			axios.post('/command/get_all/0', {}, {}).then((response) => {
				if (response.data.status == 1) {
					this.all_commands = response.data.data
				}
			})

			if (parseInt(this.$route.params.service_id) >= 1) {
				this.service_id = parseInt(this.$route.params.service_id)
				// this.$store.commit('set_go_services_id', this.service_id);
			}

			if (parseInt(this.$route.params.command_id) >= 1) {
				this.command_id = parseInt(this.$route.params.command_id)
				// this.$store.commit('set_go_commands_id', this.command_id);

				// get command variables
				axios.post('/command_var/get_all/' + parseInt(this.$route.params.command_id), {}, {}).then((response) => {
					if (response.data.status == 1) {
						this.all_command_variables = response.data.data
					}
				})
			}
			if (parseInt(this.$route.params.variable_id) >= 1) {
				// get current command
				this.record_id = parseInt(this.$route.params.variable_id)
				this.$root.loader = true
				axios.get('/command_var/browse/' + this.record_id, {}).then((response) => {
					if (response.data.status == 1) {
						this.record = response.data.data
						if (this.record.var_is_preference != undefined) {
							// console.log(this.record.var_is_preference, '----------');
							if (this.record.var_is_preference == 1) {
								this.record.var_is_preference = true
							} else {
								this.record.var_is_preference = false
							}
						}
					}
					this.$root.loader = false
					this.get_all_preferences()
				})
			} else {
				this.record_id = 0
				this.$root.loader = false
			}

			// get datasets
			this.handleOnChangeVarType({
				target: {
					value: 'datasetList',
				},
			})
		},

		watch: {
			'record.var_type_ds_id': function(newState) {
				setTimeout(this.get_ds_selected_schema, 0)
			},
		},
	}
</script>
